/**
 * Notifications des demandes de prévente (agri-product-platform) et d'achat aux négociants
 * (sales-and-market-tracking), lues avec les autres par GET /shipping/notifications.
 *
 * Acheteur : la décision du producteur ou du négociant ouvre la page où il suit ses demandes
 * (/producers, /traders). Producteur ou négociant : une demande reçue ou retirée se traite dans
 * l'espace entreprise (REACT_APP_PRODUCTION_PAGE, qui se termine par /admin).
 *
 * Contrats de prévente : la notification ouvre la fiche du contrat sur /producers (referenceId) ;
 * la cloche ne connaît pas le pays du producteur, la page prend le pays d'activité choisi. Un
 * producteur qui l'ouvre ici est renvoyé vers son espace entreprise.
 *
 * Négociations (NEGOTIATION_*) : le même type sert aux préventes et aux achats, referenceType
 * (PRESALE_REQUEST, PURCHASE_REQUEST) dit lequel, referenceId est la demande. Messages et offre
 * finale ouvrent la négociation sur /producers ou /traders (un vendeur y est renvoyé vers son
 * espace) ; le refus de l'offre finale ne concerne que le vendeur et ouvre son espace entreprise.
 */
import { ENTREPRISE_ADMIN_URL as ENTREPRISE_ADMIN } from "../../../../config/entreprisePage";

const PRESALE_TAB = `${ENTREPRISE_ADMIN}/cultivation-intentions?tab=presale`;
const TRADING_SCREEN = `${ENTREPRISE_ADMIN}/market/trading`;

const PRESALE_CONTRACT = {
    label: "Contrat de prévente",
    color: "#1b5e20",
    path: (notification) => (notification?.referenceId
        ? `/producers?contract=${encodeURIComponent(notification.referenceId)}`
        : "/producers"),
};

export const PRESALE_CONTRACT_NOTIFICATION_TYPES = [
    "PRESALE_CONTRACT_READY",
    "PRESALE_CONTRACT_SIGNED",
    "PRESALE_CONTRACT_CANCELLED",
    "PRESALE_CONTRACT_ADVANCE_PAID",
    "PRESALE_CONTRACT_DELIVERY_DECLARED",
    "PRESALE_CONTRACT_DELIVERY_CONTESTED",
    "PRESALE_CONTRACT_DELIVERY_CONFIRMED",
    "PRESALE_CONTRACT_BALANCE_PAID",
    "PRESALE_CONTRACT_ESCROW_EXECUTED",
];

export const REQUEST_NOTIFICATIONS = {
    PRESALE_REQUEST_DECIDED: { label: "Prévente", color: "#2e7d32", path: "/producers" },
    PRESALE_REQUEST_RECEIVED: { label: "Prévente", color: "#2e7d32", url: PRESALE_TAB },
    PRESALE_REQUEST_WITHDRAWN: { label: "Prévente", color: "#2e7d32", url: PRESALE_TAB },
    PURCHASE_REQUEST_DECIDED: { label: "Négoce", color: "#a0522d", path: "/traders" },
    PURCHASE_REQUEST_RECEIVED: { label: "Négoce", color: "#a0522d", url: TRADING_SCREEN },
    PURCHASE_REQUEST_WITHDRAWN: { label: "Négoce", color: "#a0522d", url: TRADING_SCREEN },
    ...Object.fromEntries(PRESALE_CONTRACT_NOTIFICATION_TYPES.map((type) => [type, PRESALE_CONTRACT])),
};

export const NEGOTIATION_NOTIFICATION_TYPES = ["NEGOTIATION_MESSAGE", "NEGOTIATION_FINAL_OFFER", "NEGOTIATION_DECLINED"];

const NEGOTIATIONS = {
    PRESALE_REQUEST: { label: "Prévente", color: "#2e7d32", page: "/producers", entreprise: PRESALE_TAB, separator: "&" },
    PURCHASE_REQUEST: { label: "Négoce", color: "#a0522d", page: "/traders", entreprise: TRADING_SCREEN, separator: "?" },
};

const negotiationNotificationOf = (notification) => {
    const kind = NEGOTIATIONS[notification.referenceType];
    if (!kind) return null;
    const id = notification.referenceId ? encodeURIComponent(notification.referenceId) : null;
    if (notification.notificationType === "NEGOTIATION_DECLINED") {
        return { label: kind.label, color: kind.color, url: id ? `${kind.entreprise}${kind.separator}negotiation=${id}` : kind.entreprise };
    }
    return { label: kind.label, color: kind.color, path: id ? `${kind.page}?negotiation=${id}` : kind.page };
};

/** Page interne (chemin) ou adresse externe où mène la notification. */
export const requestTargetOf = (request, notification) =>
    typeof request.path === "function" ? request.path(notification) : request.path;

export const requestNotificationOf = (notification) => {
    if (NEGOTIATION_NOTIFICATION_TYPES.includes(notification?.notificationType)) {
        return negotiationNotificationOf(notification);
    }
    return REQUEST_NOTIFICATIONS[notification?.notificationType] || null;
};
